import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Threshold for intersection (0-1, default: 0.1)
 * @param {string} options.rootMargin - Root margin for intersection observer (default: '0px 0px -100px 0px')
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once (default: true)
 * @param {boolean} options.disabled - If true, isVisible stays true and no IntersectionObserver runs.
 * @returns {Object} Object containing isVisible state and ref to attach to element
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -100px 0px',
    triggerOnce = true,
    disabled = false,
  } = options;

  const [isVisible, setIsVisible] = useState(disabled);
  const elementRef = useRef(null);

  useEffect(() => {
    if (disabled) return;

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add a small delay to ensure smooth animation
            setTimeout(() => {
              setIsVisible(true);
            }, 50);
            if (triggerOnce) {
              observer.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin: rootMargin || '0px 0px -150px 0px' // Trigger when element is 150px from bottom of viewport
      }
    );

    // Always observe - the IntersectionObserver will handle when to trigger
    // The rootMargin ensures elements only trigger when they're properly in view
    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce, disabled]);

  return { isVisible, elementRef };
};

export default useScrollAnimation;

